/* eslint-disable no-param-reassign */
import { produce } from 'immer';
import { COUNTRIES } from 'utils/countries';
import { millicentsToDollars } from 'utils/money';
import {
  HEADLINER_PRICES,
  PRICES_BY_PLACEMENT,
} from '../CampaignCreationView/ObjectiveStep/constants';
import {
  getMinCustomBudget,
  getPurchasablePrices,
  isPriceAvailable,
} from '../CampaignCreationView/ObjectiveStep/utils';
import { HAS_TARGETING_BY_PLACEMENT } from '../CampaignCreationView/PlacementStep/constants';
import { CATEGORIES_OPTIONS } from '../CampaignCreationView/PlacementStep/TargetingSelection/constants';
import { CampaignCreationAction, CampaignCreationState } from './types';
import { getCampaignEpisode } from './utils';

export const defaultState: CampaignCreationState = {
  values: {
    episode: undefined,
    sponsorPlacement: undefined,
    targetCategories: [CATEGORIES_OPTIONS[0].itemValue],
    targetCountryCodes: [COUNTRIES[0].itemValue],
    campaignBudgetMilliCent: Number(HEADLINER_PRICES[0].value),
    campaignDuration: undefined,
    isTargetingContactRequest: false,
  },
  step: 'episode',
  subStep: undefined,
  pricesList: HEADLINER_PRICES,
  isCustomBudget: false,
  minCustomBudget: 0,
  isValidCustomBudget: false,
};

export default function reducer(
  state: CampaignCreationState,
  action: CampaignCreationAction,
) {
  switch (action.type) {
    case 'CHANGE_VALUES': {
      const { payload: newValues } = action;

      return produce(state, (d) => {
        const updatedValues = {
          ...d.values,
          ...newValues,
        };

        d.values = updatedValues;
        d.isValidCustomBudget =
          Number(updatedValues.campaignBudgetMilliCent) >= d.minCustomBudget;
      });
    }

    case 'TOGGLE_IS_CUSTOM_BUDGET': {
      const { payload: isCustomBudget } = action;

      return produce(state, (d) => {
        d.isCustomBudget = isCustomBudget ?? !d.isCustomBudget;
      });
    }

    case 'CHANGE_PLACEMENT': {
      const {
        payload: { placement: nextPlacement, isProGrow },
      } = action;

      return produce(state, (d) => {
        const { values } = d;
        const hasTargeting = HAS_TARGETING_BY_PLACEMENT[nextPlacement];
        const newPricesList = PRICES_BY_PLACEMENT[nextPlacement];
        const purchasablePrices = getPurchasablePrices(
          newPricesList,
          isProGrow,
        );
        const newMinCustomBudget = getMinCustomBudget(newPricesList);
        const isSelectedBudgetAvailable = isPriceAvailable(
          purchasablePrices,
          values.campaignBudgetMilliCent,
        );

        values.sponsorPlacement = nextPlacement;
        values.targetCategories = hasTargeting
          ? [CATEGORIES_OPTIONS[0].itemValue]
          : [];
        values.targetCountryCodes = hasTargeting
          ? [COUNTRIES[0].itemValue]
          : [];

        if (!d.isCustomBudget && !isSelectedBudgetAvailable) {
          values.campaignBudgetMilliCent = purchasablePrices[0]?.value;
        }

        d.pricesList = newPricesList;
        d.minCustomBudget = newMinCustomBudget;
        d.isValidCustomBudget =
          Number(values.campaignBudgetMilliCent) >= newMinCustomBudget;
      });
    }

    case 'CHANGE_CHECKBOX_VALUE': {
      return produce(state, (d) => {
        const { values } = d;
        values.isTargetingContactRequest = !values.isTargetingContactRequest;
      });
    }

    case 'RE_RUN_CAMPAIGN': {
      const {
        payload: { campaign, isProGrow },
      } = action;

      const {
        sponsorPlacement,
        targetCategories,
        targetCountryCodes,
        campaignBudgetMilliCent,
        campaignDuration,
        isTargetingContactRequest,
      } = campaign;

      const categories = targetCategories ?? [CATEGORIES_OPTIONS[0].itemValue];
      const countries = targetCountryCodes ?? [COUNTRIES[0].itemValue];
      const contactRequest = isTargetingContactRequest ?? false;
      const newPricesList =
        PRICES_BY_PLACEMENT[sponsorPlacement ?? 'headlinerApp'];
      const purchasablePrices = getPurchasablePrices(newPricesList, isProGrow);
      const newMinCustomBudget = getMinCustomBudget(newPricesList);
      const isCustomBudget = !isPriceAvailable(
        newPricesList,
        campaignBudgetMilliCent,
      );
      const isSelectedBudgetAvailable = isPriceAvailable(
        purchasablePrices,
        campaignBudgetMilliCent,
      );
      const presetBudget = isSelectedBudgetAvailable
        ? campaignBudgetMilliCent
        : purchasablePrices[0]?.value;
      const budget = isCustomBudget
        ? millicentsToDollars(campaignBudgetMilliCent ?? 0)
        : presetBudget;

      return produce(state, (d) => {
        d.pricesList = newPricesList;
        d.minCustomBudget = newMinCustomBudget;
        d.isCustomBudget = isCustomBudget;
        d.isValidCustomBudget = Number(budget) >= newMinCustomBudget;

        d.values = {
          ...d.values,
          episode: getCampaignEpisode(campaign),
          sponsorPlacement,
          targetCategories: categories,
          targetCountryCodes: countries,
          campaignBudgetMilliCent: budget,
          campaignDuration,
          isTargetingContactRequest: contactRequest,
        };
      });
    }

    case 'GO_TO_STEP': {
      const {
        payload: { step, subStep },
      } = action;

      return produce(state, (d) => {
        d.step = step;
        d.subStep = subStep;
      });
    }

    case 'RESET_VALUES': {
      return defaultState;
    }

    default:
      return state;
  }
}
